// externals
import type { Middleware } from 'redux';
import { nanoid } from 'nanoid';
import { sessionUtils } from '@makemydeal/dr-shared-ui-utils';

// telemetry
import { getRegisteredSelectors, type RegisteredSelector } from '../telemetry/selectorRegistry';
import { resolveJoinActionIndex, resolveJoinCorrelationId, nextEmitSequence } from './correlationMiddleware';
import { matchesIgnoredActionPrefix, TELEMETRY_IGNORED_ACTION_PREFIXES } from './telemetryIgnoredActions';

/**
 * Configuration for the selector delta telemetry middleware.
 *
 * This middleware runs **immediately before** `stateSnapshotTelemetryMiddleware`
 * in the chain (guaranteed structurally by the store configurer, which appends
 * the telemetry middlewares after all business middleware, and covered by a
 * store-configurer unit test).
 * On every non-ignored dispatch it walks the selector registry, evaluates each
 * registered selector before and after `next(action)`, and emits:
 *
 *  - `SelectorSnapshot` — full-registry map, emitted at cadence (default 100
 *    dispatches). Consumers replay forward from an anchor.
 *  - `SelectorDelta` — per-action row containing ALL changed keys, one entry
 *    per changed selector. Scalars carry `{k, b, a}`; collections carry
 *    `{k, h}` (FNV-8 hash only).
 *  - `SelectorTelemetryError` — swallowed errors are surfaced as a low-volume
 *    diagnostic row.
 *
 * @template TState - The Redux state tree type.
 */
export type SelectorDeltaConfig<TState> = {
    /**
     * Whether telemetry is enabled. Can be:
     *   - `boolean`: static enable/disable.
     *   - `(state) => boolean`: dynamic gate on state (typically a feature
     *     toggle selector). Errors thrown by this function are treated as
     *     "disabled".
     *
     * Recommended: gate on `isStateSnapshotTelemetryEnabled(state)`; default `false`.
     */
    enabled?: boolean | ((state: TState) => boolean);

    /**
     * Identifier for the Redux store emitting these events (e.g. `'dash-store'`).
     */
    storeId: string;

    /**
     * Emit a full-registry `SelectorSnapshot` every N actions. A snapshot is
     * also emitted on the first non-ignored dispatch and after any capture
     * error, so replay never depends on receiving delta 0. Default: `100`.
     * Set to `1` in tests.
     */
    snapshotCadence?: number;

    /**
     * Maximum serialized characters per PageAction attribute value. New Relic
     * Browser silently drops PageAction string attributes larger than ~4096
     * chars. Anchors of a large registry (~100 selectors) can easily exceed
     * this; chunking splits them into N rows that share a `chunkGroupId`.
     * Deltas are almost always small (one action typically changes 1-6
     * selectors) so chunking is rare on the delta path. Default: `3_000`.
     */
    maxChunkBytes?: number;

    /**
     * Extract per-event business context (dealXgId, offerId, etc.) from
     * state. Same shape as `correlationMiddleware` /
     * `stateSnapshotTelemetryMiddleware`. Errors are swallowed.
     */
    getBusinessContext?: (state: TState) => Record<string, unknown>;

    /**
     * Optional readiness gate. When provided, the middleware skips counting
     * dispatches and emitting any telemetry until this predicate returns
     * `true`. Once true it is never called again for the life of the store.
     *
     * Purpose: the first `SelectorSnapshot` (emitted on the first eligible
     * dispatch) is the replay anchor. That first dispatch typically happens
     * BEFORE bootstrap has populated the business identifiers (dealXgId,
     * offerId) that per-deal replay tooling filters on — so without this gate
     * the anchor lands with `dealXgId=""` and is invisible to per-deal
     * dashboards, breaking selector replay for the whole session until the
     * next cadenced snapshot. Deferring the counter + emission until business
     * context is ready guarantees the anchor carries those identifiers.
     *
     * Kept intentionally identical to `stateSnapshotTelemetryMiddleware`'s
     * `waitForReady` so the two tail middlewares flip ready on the same
     * dispatch and stay aligned on the shared actionIndex join key.
     *
     * Called with the pre-reducer state on every dispatch until it returns
     * `true`. Errors are treated as `false` (still waiting).
     */
    waitForReady?: (state: TState) => boolean;

    /**
     * Whether to emit a `SelectorDelta` row for dispatches that changed NOTHING
     * in the registry. Default `false` (recommendation #5).
     *
     * A dispatch with no selector change is already countable via
     * `ReduxAction` on the shared `actionIndex`; emitting one empty
     * (`deltaJson: '[]'`) PageAction per non-ignored dispatch multiplies New
     * Relic ingest for no diagnostic value and risks hitting per-page-view
     * harvest caps (which would drop the rows that DO carry a change). Set to
     * `true` only when replay tooling specifically needs a positive
     * "no-change" marker per dispatch rather than inferring it from the absence
     * of a `SelectorDelta` at an `actionIndex` present in `ReduxAction`.
     */
    emitEmptyDeltas?: boolean;

    /**
     * Action-type prefixes to skip. Injectable; defaults to
     * {@link TELEMETRY_IGNORED_ACTION_PREFIXES} (RSAA envelope only). Router /
     * store-init actions ARE walked by default. See telemetryIgnoredActions.
     */
    ignoredActionPrefixes?: readonly string[];
};

/**
 * PageAction event names emitted by this middleware. Referenced by NRQL and
 * the Python replay CLI — do not rename without a coordinated migration.
 */
export const SELECTOR_TELEMETRY_EVENTS = Object.freeze({
    SNAPSHOT: 'SelectorSnapshot',
    DELTA: 'SelectorDelta',
    HEALTH: 'SelectorTelemetryHealth'
});

const DEFAULT_SNAPSHOT_CADENCE = 100;
const DEFAULT_MAX_CHUNK_BYTES = 3_000;
// Ring-buffer cap for walk-timing samples so a long-lived session doesn't grow
// the timing array without bound. Percentiles are order-independent, so the
// ring can overwrite oldest-first without affecting p95/p99 accuracy.
const MAX_WALK_TIMING_SAMPLES = 1_000;

/**
 * Return the value at the given percentile (1..99) from an ALREADY-SORTED
 * ascending numeric array. Returns 0 on empty input. Uses nearest-rank so
 * results are meaningful even at small sample sizes.
 */
export const percentile = (sortedAsc: readonly number[], p: number): number => {
    if (sortedAsc.length === 0) return 0;
    const rank = Math.max(0, Math.min(sortedAsc.length - 1, Math.ceil((p / 100) * sortedAsc.length) - 1));
    return sortedAsc[rank];
};

// Match the state-snapshot / correlation middleware ignore list to keep the
// event-count budget aligned. Falsy-type policy is LOCAL (tail middlewares
// require a type string, so a type-less action is ignored); the shared prefix
// decision is delegated to `matchesIgnoredActionPrefix` with an injectable set.
const shouldIgnoreAction = (actionType: unknown, prefixes: readonly string[]): boolean => {
    if (!actionType) return true;
    return matchesIgnoredActionPrefix(String(actionType), prefixes);
};

/**
 * Safely emit a New Relic PageAction. Silent failure — never break the app.
 */
const emitPageAction = (name: string, payload: Record<string, unknown>): void => {
    /* istanbul ignore next: window guard for SSR */
    if (typeof window === 'undefined') return;
    try {
        window.newrelic?.addPageAction?.(name, payload as Record<string, string | number>);
    } catch {
        // Telemetry loss is preferred over app breakage.
    }
};

/**
 * Serialize a value for New Relic transport. Returns an empty string on
 * serialization failure (e.g. circular refs, non-serializable state).
 */
/* istanbul ignore next: catch is defence-in-depth for exotic JSON inputs */
const serializeForNr = (value: unknown): string => {
    try {
        return JSON.stringify(value) ?? '';
    } catch {
        return '';
    }
};

/**
 * Stable stringify for hash input. Sorts object keys recursively so
 * two collections with the same content but different insertion order
 * hash identically. Returns `''` on serialization failure (e.g. circular
 * refs the WeakSet-based cycle break didn't catch, or non-serializable
 * exotic values) rather than throwing.
 */
/* istanbul ignore next: catch is defence-in-depth; WeakSet-based sortReplacer breaks cycles */
const stableStringify = (value: unknown): string => {
    try {
        return JSON.stringify(value, sortReplacer(new WeakSet())) ?? '';
    } catch {
        return '';
    }
};

const sortReplacer =
    (seen: WeakSet<object>) =>
    (_key: string, val: unknown): unknown => {
        if (val === null || typeof val !== 'object') return val;
        if (seen.has(val as object)) return null; // break cycles
        seen.add(val as object);
        if (Array.isArray(val)) return val;
        const out: Record<string, unknown> = {};
        const keys = Object.keys(val as Record<string, unknown>).sort();
        for (const k of keys) out[k] = (val as Record<string, unknown>)[k];
        return out;
    };

/**
 * 32-bit FNV-1a hash of a UTF-8 string, rendered as an 8-char hex string.
 *
 * FNV-1a is chosen because it's tiny (~10 lines), zero-dependency, and has
 * good distribution for short-to-medium inputs. Collision risk is acceptable
 * because the hash is a "did this collection change?" signal, not a
 * cryptographic identity — a rare collision means we'd miss one collection
 * change delta, which the anchor cadence corrects within `snapshotCadence`
 * dispatches.
 */
export const fnv1a8 = (input: string): string => {
    let hash = 0x811c9dc5; // 32-bit FNV offset basis
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        // Multiply by FNV prime with 32-bit wrap. Uses Math.imul for
        // correct 32-bit signed multiplication.
        hash = Math.imul(hash, 0x01000193);
    }
    // Convert to unsigned 32-bit then pad to 8 hex chars.
    return (hash >>> 0).toString(16).padStart(8, '0');
};

/**
 * On-wire delta entry for a scalar-shaped selector. `b`/`a` follow the same
 * absence semantics as {@link ../utils/stateSnapshotDelta stateSnapshotDelta}:
 * a missing key means the value was `undefined` on that side.
 */
export type ScalarDeltaEntry = {
    k: string;
    b?: unknown;
    a?: unknown;
};

/**
 * On-wire delta entry for a collection-shaped selector. Only the hash of the
 * post-dispatch value is included; the pre-dispatch value is implicit (if
 * the hash changed, the value changed).
 */
export type CollectionDeltaEntry = {
    k: string;
    h: string;
};

export type SelectorDeltaEntry = ScalarDeltaEntry | CollectionDeltaEntry;

/**
 * Evaluate a single registered selector safely. Errors are surfaced as
 * `undefined` so a broken selector never breaks the middleware.
 */
const safeEval = (entry: RegisteredSelector, state: unknown): unknown => {
    try {
        return entry.selector(state);
    } catch {
        return undefined;
    }
};

/**
 * Emit a possibly-chunked event. Mirrors the state-snapshot middleware
 * chunking approach; see that file for the wire-shape rationale.
 */
const emitPossiblyChunked = (
    eventName: string,
    payloadFieldName: string,
    payloadJson: string,
    sharedAttrs: Record<string, unknown>,
    maxChunkBytes: number,
    newChunkGroupId: () => string
): void => {
    const cap = Math.max(1, maxChunkBytes);
    if (payloadJson.length <= cap) {
        emitPageAction(eventName, {
            ...sharedAttrs,
            [payloadFieldName]: payloadJson
        });
        return;
    }
    const chunkGroupId = newChunkGroupId();
    const chunkCount = Math.ceil(payloadJson.length / cap);
    for (let chunkIndex = 0; chunkIndex < chunkCount; chunkIndex++) {
        const start = chunkIndex * cap;
        const chunkPayload = payloadJson.slice(start, start + cap);
        emitPageAction(eventName, {
            ...sharedAttrs,
            chunkGroupId,
            chunkIndex,
            chunkCount,
            chunkPayload
        });
    }
};

/**
 * Compare two values for scalar-shape purposes. Uses `Object.is` for
 * primitives (handles NaN correctly) and stable-stringify equality for
 * shallow objects. Collection-shape selectors do not use this — they hash
 * both sides and compare hashes.
 */
const scalarEqual = (before: unknown, after: unknown): boolean => {
    if (Object.is(before, after)) return true;
    // For shallow objects registered as scalar (rare but permitted), compare
    // via stable stringify.
    if (before !== null && after !== null && typeof before === 'object' && typeof after === 'object') {
        return stableStringify(before) === stableStringify(after);
    }
    return false;
};

/* istanbul ignore next: catch is defence-in-depth; getAppSessionId is a pure getter */
const safeGetSessionId = (): string => {
    try {
        return sessionUtils.getAppSessionId?.() ?? '';
    } catch {
        return '';
    }
};

/**
 * High-resolution timestamp for walk-timing measurement. `performance.now()` is
 * present in every browser and in jsdom, but the rest of this file guards
 * `window`/`document` for non-DOM hosts, so guard `performance` symmetrically and
 * fall back to `Date.now()` (millisecond resolution is fine — we only compute
 * percentiles for a coarse health signal).
 */
/* istanbul ignore next: fallback only taken on exotic/SSR hosts without performance */
const safeNow = (): number =>
    typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();

/**
 * Create the selector delta telemetry middleware.
 *
 * Emitted event shapes:
 *
 *   SelectorSnapshot (single-row, snapshot fits under `maxChunkBytes`):
 *     {
 *       eventId, storeId, sessionId, correlationId, actionType,
 *       actionIndex, sizeBytes, keyCount,
 *       snapshotJson: "<serialized full registry map>",
 *       ...businessContext
 *     }
 *
 *   SelectorSnapshot (chunked form):
 *     { ...common, chunkGroupId, chunkIndex, chunkCount, chunkPayload }
 *
 *   SelectorDelta (single-row form):
 *     {
 *       eventId, storeId, sessionId, correlationId, actionType,
 *       actionIndex, sizeBytes, changedKeyCount,
 *       deltaJson: "<serialized array of ScalarDeltaEntry|CollectionDeltaEntry>",
 *       ...businessContext
 *     }
 *
 *   SelectorTelemetryHealth (emitted once per session at `beforeunload`):
 *     {
 *       eventId, storeId, sessionId,
 *       walkP95Ms, walkP99Ms,
 *       dispatchesObserved, deltasEmitted, sampleCount
 *     }
 */
export const createSelectorDeltaTelemetryMiddleware = <TState = unknown>(
    config: SelectorDeltaConfig<TState>
): Middleware<Record<string, never>, TState> => {
    const {
        enabled = false,
        storeId,
        snapshotCadence = DEFAULT_SNAPSHOT_CADENCE,
        maxChunkBytes = DEFAULT_MAX_CHUNK_BYTES,
        getBusinessContext,
        waitForReady,
        emitEmptyDeltas = false,
        ignoredActionPrefixes = TELEMETRY_IGNORED_ACTION_PREFIXES
    } = config;

    let actionIndex = 0;
    let forceSnapshot = true;
    // Readiness latch — once flipped true it stays true. See `waitForReady`
    // config docs. If no `waitForReady` was provided, treated as always ready.
    let isReady = !waitForReady;

    // Health self-measurement (AC-6). Walk timings are recorded per
    // non-ignored dispatch into a fixed-size ring; percentiles computed on
    // teardown flush.
    const walkTimings: number[] = [];
    let walkTimingsWriteIndex = 0;
    let dispatchesObserved = 0;
    let deltasEmitted = 0;
    let healthEmitted = false;

    // Cleanup for the teardown listeners registered below. Reassigned to the
    // real remover when a DOM is present; the health flush calls it so the
    // listeners don't outlive the one-shot emission (matters for tests that
    // construct many middleware instances).
    /* istanbul ignore next: replaced whenever window exists (always in jsdom/browser) */
    let removeHealthListeners: () => void = () => undefined;

    // Record a walk timing into the bounded ring buffer. Oldest samples are
    // overwritten once the ring is full — fine for percentiles, which don't
    // depend on sample order.
    const recordWalkTiming = (ms: number): void => {
        if (walkTimings.length < MAX_WALK_TIMING_SAMPLES) {
            walkTimings.push(ms);
        } else {
            walkTimings[walkTimingsWriteIndex] = ms;
            walkTimingsWriteIndex = (walkTimingsWriteIndex + 1) % MAX_WALK_TIMING_SAMPLES;
        }
    };

    const emitHealth = (): void => {
        if (healthEmitted) return;
        healthEmitted = true;
        const sorted = [...walkTimings].sort((a, b) => a - b);
        emitPageAction(SELECTOR_TELEMETRY_EVENTS.HEALTH, {
            eventId: nanoid(),
            storeId,
            sessionId: safeGetSessionId(),
            walkP95Ms: percentile(sorted, 95),
            walkP99Ms: percentile(sorted, 99),
            dispatchesObserved,
            deltasEmitted,
            sampleCount: walkTimings.length
        });
        // One-shot event: drop the teardown listeners so repeated factory calls
        // (e.g. per-test store construction) don't accumulate them.
        removeHealthListeners();
    };

    // Register teardown flushes exactly once per middleware factory call. The NR
    // Browser agent buffers PageActions and flushes them via its own
    // `sendBeacon` on unload, so calling `addPageAction` inside these handlers
    // is the effective way to ship the health event during page teardown.
    // `beforeunload` alone is unreliable on mobile / bfcache, so we also listen
    // for `pagehide` and `visibilitychange`→hidden (recommendation #7).
    // `emitHealth` is idempotent, so overlapping handlers are safe.
    /* istanbul ignore else: SSR guard */
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        const flushHealth = (): void => emitHealth();
        const onVisibilityChange = (): void => {
            /* istanbul ignore else: only flush on transition to hidden */
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                emitHealth();
            }
        };
        window.addEventListener('beforeunload', flushHealth);
        window.addEventListener('pagehide', flushHealth);
        window.addEventListener('visibilitychange', onVisibilityChange);
        removeHealthListeners = (): void => {
            window.removeEventListener('beforeunload', flushHealth);
            window.removeEventListener('pagehide', flushHealth);
            window.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }

    const isEnabled = (state: TState): boolean => {
        try {
            return typeof enabled === 'function' ? enabled(state) : enabled;
        } catch {
            return false;
        }
    };

    const safeBusinessContext = (state: TState): Record<string, unknown> => {
        if (!getBusinessContext) return {};
        try {
            return getBusinessContext(state) ?? {};
        } catch {
            return {};
        }
    };

    return (store) => (next) => (action) => {
        const preState = store.getState();
        if (!isEnabled(preState)) {
            return next(action);
        }
        if (!action) {
            return next(action);
        }
        if (shouldIgnoreAction((action as { type?: unknown }).type, ignoredActionPrefixes)) {
            return next(action);
        }

        // Readiness gate — skip counting/emitting until business context is
        // populated. See `waitForReady` config docs. This is what makes the
        // first SelectorSnapshot anchor carry populated business identifiers
        // (dealXgId etc.) so per-deal replay tooling can filter on them. Kept
        // identical to stateSnapshotTelemetryMiddleware so both tail middlewares
        // flip ready on the same dispatch.
        if (!isReady) {
            let ready = false;
            try {
                ready = !!waitForReady!(preState);
            } catch {
                ready = false;
            }
            if (!ready) {
                return next(action);
            }
            isReady = true;
        }

        const actionType = String((action as { type: unknown }).type);
        const eventId = nanoid();
        const sessionId = safeGetSessionId();

        // Resolve the join ordinal + correlationId WITHOUT mutating the action.
        // Prefer the values stamped by correlationMiddleware (via action.meta).
        // RSAA lifecycle FSAs (e.g. OFFER_SAVE_SUCCESS) are dispatched by
        // redux-api-middleware via next(), downstream of correlationMiddleware,
        // so they arrive here WITHOUT meta.actionIndex/correlationId. For those,
        // resolveJoin* draws the next ordinal from correlationMiddleware's shared
        // per-store counter and memoizes it on THIS action object; the adjacent
        // stateSnapshot middleware receives the very same action reference (we
        // pass it through untouched) and reads back the identical join key
        // instead of drifting on an independent counter. The action delivered to
        // reducers is never modified. See DE753626 and recommendation #1.
        const emittedActionIndex = resolveJoinActionIndex(store, action);
        const correlationId = resolveJoinCorrelationId(action);

        // Evaluate every registered selector on pre-state. Per-selector
        // errors are absorbed by `safeEval` and surface as `undefined`
        // values, so we don't need an outer try/catch around the loop.
        const entries = getRegisteredSelectors();
        const walkStart = safeNow();
        const beforeValues = new Array(entries.length);
        for (let i = 0; i < entries.length; i++) {
            beforeValues[i] = safeEval(entries[i], preState);
        }

        const result = next(action);

        const postState = store.getState();
        const afterValues = new Array(entries.length);
        for (let i = 0; i < entries.length; i++) {
            afterValues[i] = safeEval(entries[i], postState);
        }
        recordWalkTiming(safeNow() - walkStart);
        dispatchesObserved += 1;

        // Stamp PRE-reducer business context (from preState) to match
        // correlationMiddleware. Both emitters then denormalize the SAME
        // business identifiers onto every row sharing this actionIndex, so a
        // NRQL facet (e.g. WHERE offerId = X) selects the correlation and delta
        // rows for an identifier-mutating dispatch (offer/scenario switch)
        // consistently instead of splitting them across pre/post values.
        const businessContext = safeBusinessContext(preState);
        // Private per-instance dispatch counter drives snapshot cadence + health
        // sampling; it counts dispatches THIS middleware observes, independent of
        // the shared join ordinal (emittedActionIndex) resolved above.
        actionIndex += 1;

        // First dispatch always anchors via `forceSnapshot`; thereafter emit on
        // the cadence. (`forceSnapshot` starts true and is cleared after the
        // first snapshot, so an explicit `actionIndex === 1` check is redundant.)
        const shouldEmitSnapshot = forceSnapshot || actionIndex % Math.max(1, snapshotCadence) === 0;

        // No outer try/catch: every internal call swallows on failure
        // (safeEval, emitPageAction, serializeForNr, stableStringify).
        // An outer catch would be dead defence-in-depth and untestable.
        const commonAttrs: Record<string, unknown> = {
            eventId,
            storeId,
            sessionId,
            correlationId,
            actionType,
            actionIndex: emittedActionIndex,
            // Emit-time ordinal (US2056149): tail rows are emitted post-reducer,
            // so emitSequence encodes true reducer-completion order even when it
            // diverges from the initiation-order actionIndex (nested dispatch).
            emitSequence: nextEmitSequence(store),
            ...businessContext
        };

        // Build the delta array once. Reused as the c-companion payload
        // in the snapshot event and as the standalone SelectorDelta row.
        const delta: SelectorDeltaEntry[] = [];
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const before = beforeValues[i];
            const after = afterValues[i];
            if (entry.shape === 'scalar') {
                if (!scalarEqual(before, after)) {
                    const rec: ScalarDeltaEntry = { k: entry.key };
                    if (before !== undefined) rec.b = before;
                    if (after !== undefined) rec.a = after;
                    delta.push(rec);
                }
            } else {
                // collection
                const beforeHash = fnv1a8(stableStringify(before));
                const afterHash = fnv1a8(stableStringify(after));
                if (beforeHash !== afterHash) {
                    delta.push({ k: entry.key, h: afterHash });
                }
            }
        }
        const changedKeyCount = delta.length;

        if (shouldEmitSnapshot) {
            // Build a full-registry map: { key: value-or-hash }.
            const snapshotMap: Record<string, unknown> = {};
            for (let i = 0; i < entries.length; i++) {
                const entry = entries[i];
                const value = afterValues[i];
                if (entry.shape === 'scalar') {
                    snapshotMap[entry.key] = { v: value };
                } else {
                    snapshotMap[entry.key] = { h: fnv1a8(stableStringify(value)) };
                }
            }
            const snapshotJson = serializeForNr(snapshotMap);
            emitPossiblyChunked(
                SELECTOR_TELEMETRY_EVENTS.SNAPSHOT,
                'snapshotJson',
                snapshotJson,
                {
                    ...commonAttrs,
                    sizeBytes: snapshotJson.length,
                    keyCount: entries.length
                },
                maxChunkBytes,
                nanoid
            );
            forceSnapshot = false;
        }

        // Companion delta. By default a dispatch that changed NOTHING emits NO
        // row (recommendation #5): the dispatch is already countable via
        // ReduxAction on the shared actionIndex, and one empty PageAction per
        // dispatch multiplies NR ingest for no diagnostic value. Opt in with
        // `emitEmptyDeltas` to restore a lightweight `[]` marker per dispatch.
        if (changedKeyCount === 0) {
            if (emitEmptyDeltas) {
                emitPageAction(SELECTOR_TELEMETRY_EVENTS.DELTA, {
                    ...commonAttrs,
                    changedKeyCount: 0,
                    sizeBytes: 2,
                    deltaJson: '[]'
                });
                deltasEmitted += 1;
            }
        } else {
            const deltaJson = serializeForNr(delta);
            emitPossiblyChunked(
                SELECTOR_TELEMETRY_EVENTS.DELTA,
                'deltaJson',
                deltaJson,
                {
                    ...commonAttrs,
                    changedKeyCount,
                    sizeBytes: deltaJson.length
                },
                maxChunkBytes,
                nanoid
            );
            deltasEmitted += 1;
        }

        return result;
    };
};
